import { format } from "date-fns";

import type { IChronogramTick } from "../../forecasting/interfaces";
import type { IChartData } from "../../scheme/components/analytics/interfaces";
import type { IMultiAnalysisDataMark, IMultiAnalysisTimeRange } from "../types";
import { normalizeTimeRange, parseMultiAnalysisChartTime, timeToRatio } from "./range-frame";
import { getTimePanelMeasureKey } from "./time-panel-measures";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const HOUR_IN_MS = 60 * 60 * 1000;

const getTimePanelTickPattern = (durationMs: number): string => {
  if (durationMs <= DAY_IN_MS) {
    return "HH:mm";
  }

  if (durationMs <= 7 * DAY_IN_MS) {
    return "dd.MM HH:mm";
  }

  if (durationMs <= 90 * DAY_IN_MS) {
    return "dd.MM";
  }

  if (durationMs <= 366 * DAY_IN_MS) {
    return "MM.yyyy";
  }

  return "yyyy";
};

const getTimePanelTickInterval = (durationMs: number): number => {
  if (durationMs <= DAY_IN_MS) {
    return HOUR_IN_MS;
  }

  if (durationMs <= 7 * DAY_IN_MS) {
    return DAY_IN_MS;
  }

  const targetTickCount = 7;
  const rawInterval = durationMs / targetTickCount;

  if (durationMs <= 90 * DAY_IN_MS) {
    return Math.max(DAY_IN_MS, Math.round(rawInterval / DAY_IN_MS) * DAY_IN_MS);
  }

  if (durationMs <= 366 * DAY_IN_MS) {
    const monthMs = 30 * DAY_IN_MS;

    return Math.max(monthMs, Math.round(rawInterval / monthMs) * monthMs);
  }

  const yearMs = 365 * DAY_IN_MS;

  return Math.max(yearMs, Math.round(rawInterval / yearMs) * yearMs);
};

export const buildTimePanelChronogramTicks = (globalRange: IMultiAnalysisTimeRange): IChronogramTick[] => {
  const bounds = normalizeTimeRange(globalRange);

  if (!bounds) {
    return [];
  }

  const duration = bounds.end - bounds.start;

  if (duration <= 0) {
    return [];
  }

  const intervalMs = getTimePanelTickInterval(duration);
  const pattern = getTimePanelTickPattern(duration);
  const ticks: IChronogramTick[] = [];

  let tickTime = Math.floor(bounds.start / intervalMs) * intervalMs;

  while (tickTime <= bounds.end) {
    if (tickTime >= bounds.start) {
      const ratio = (tickTime - bounds.start) / duration;

      ticks.push({
        key: `time-panel-tick-${tickTime}`,
        left: ratio * 100,
        label: format(new Date(tickTime), pattern),
      });
    }

    tickTime += intervalMs;
  }

  return ticks;
};

export interface ITimePanelCoverageSegment {
  key: string;
  leftRatio: number;
  widthRatio: number;
  hasData: boolean;
}

export const buildTimePanelCoverageSegments = (
  trendSeries: IChartData[],
  globalRange: IMultiAnalysisTimeRange,
  segmentCount = 80,
): ITimePanelCoverageSegment[] => {
  const bounds = normalizeTimeRange(globalRange);

  if (!bounds) {
    return [];
  }

  const duration = bounds.end - bounds.start;

  if (duration <= 0) {
    return [];
  }

  const timestamps = trendSeries.flatMap(series =>
    series.items
      .map(({ date }) => parseMultiAnalysisChartTime(date))
      .filter(timestamp => Number.isFinite(timestamp) && timestamp >= bounds.start && timestamp <= bounds.end),
  );

  const count = Math.max(16, Math.min(segmentCount, 160));

  return Array.from({ length: count }, (_, index) => {
    const leftRatio = index / count;
    const rightRatio = (index + 1) / count;
    const segmentStart = bounds.start + duration * leftRatio;
    const segmentEnd = bounds.start + duration * rightRatio;
    const hasData = timestamps.some(timestamp => timestamp >= segmentStart && timestamp < segmentEnd);

    return {
      key: `coverage-${index}`,
      leftRatio,
      widthRatio: 1 / count,
      hasData,
    };
  });
};

export interface IVisibleTrendSeriesPoint {
  idPoint: number;
  idMeasure: number | null;
  date: string;
  x: number;
  y: number;
  parameterName?: string;
  seriesName: string;
  measureKey: string | null;
}

const doesMeasureMatchVertex = (vertex: IVisibleTrendSeriesPoint, mark: IMultiAnalysisDataMark): boolean => {
  if (vertex.idMeasure != null && vertex.idMeasure === mark.idMeasure) {
    return true;
  }

  const measureName = mark.measureName.trim();

  if (!measureName) {
    return true;
  }

  if (vertex.parameterName && (measureName === vertex.parameterName || measureName.includes(vertex.parameterName))) {
    return true;
  }

  return vertex.seriesName.includes(measureName) || measureName.includes(vertex.seriesName);
};

export const buildVisibleTrendSeriesPoints = (
  normalizedSeries: ReadonlyArray<{
    idPoint?: number;
    points: ReadonlyArray<{ date: string; x: number; y: number }>;
  }>,
  trendSeries: readonly IChartData[],
  minPoints = 1,
): IVisibleTrendSeriesPoint[] =>
  normalizedSeries.flatMap((series, index) => {
    if (series.points.length < minPoints || series.idPoint == null) {
      return [];
    }

    const meta = trendSeries[index];

    return series.points.map(point => ({
      idPoint: series.idPoint!,
      idMeasure: Number.isFinite(Number(meta?.idMeasure)) ? Number(meta.idMeasure) : null,
      date: point.date,
      x: point.x,
      y: point.y,
      parameterName: meta?.parameterName,
      seriesName: meta?.name ?? "",
      measureKey: getTimePanelMeasureKey(series.idPoint, meta?.parameterName, meta?.name, meta?.idMeasure),
    }));
  });

export const resolveDataMarkTrendVertex = (
  mark: IMultiAnalysisDataMark,
  vertices: readonly IVisibleTrendSeriesPoint[],
): IVisibleTrendSeriesPoint | null => {
  const markTimestamp = parseMultiAnalysisChartTime(mark.date);

  if (!Number.isFinite(markTimestamp)) {
    return null;
  }

  const candidates = vertices.filter(
    vertex => vertex.idPoint === mark.idPoint && parseMultiAnalysisChartTime(vertex.date) === markTimestamp,
  );

  if (!candidates.length) {
    return null;
  }

  if (candidates.length === 1) {
    return candidates[0];
  }

  const idMeasureMatched = candidates.filter(vertex => vertex.idMeasure != null && vertex.idMeasure === mark.idMeasure);

  if (idMeasureMatched.length === 1) {
    return idMeasureMatched[0];
  }

  if (idMeasureMatched.length > 1) {
    return idMeasureMatched[0];
  }

  const measureMatched = candidates.filter(vertex => doesMeasureMatchVertex(vertex, mark));

  if (measureMatched.length === 1) {
    return measureMatched[0];
  }

  if (measureMatched.length > 1) {
    return measureMatched[0];
  }

  return candidates[0] ?? null;
};

export const resolveDataMarkPlotPosition = (
  mark: IMultiAnalysisDataMark,
  globalRange: IMultiAnalysisTimeRange,
  plotLeft: number,
  plotWidth: number,
  plotTop: number,
  trendHeight: number,
  vertex: IVisibleTrendSeriesPoint | null,
): { x: number; y: number } => {
  if (vertex) {
    return { x: vertex.x, y: vertex.y };
  }

  const timestamp = parseMultiAnalysisChartTime(mark.date);
  const ratio = Number.isFinite(timestamp) ? Math.min(Math.max(timeToRatio(timestamp, globalRange), 0), 1) : 1;

  return {
    x: plotLeft + ratio * plotWidth,
    y: plotTop + trendHeight / 2,
  };
};

const clampValue = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

export interface ITimePanelDataMarkDimensions {
  size: number;
  offset: number;
  strokeWidth: number;
  selectedStrokeWidth: number;
}

const TIME_PANEL_DATA_MARK_REFERENCE_TREND_HEIGHT = 40;
const TIME_PANEL_DATA_MARK_MIN_SIZE = 7;
const TIME_PANEL_DATA_MARK_MAX_SIZE = 18;
const TIME_PANEL_DATA_MARK_MAX_TREND_HEIGHT = 160;

export const computeTimePanelDataMarkDimensions = (trendHeight: number): ITimePanelDataMarkDimensions => {
  const ratio = clampValue(
    (trendHeight - TIME_PANEL_DATA_MARK_REFERENCE_TREND_HEIGHT) /
      (TIME_PANEL_DATA_MARK_MAX_TREND_HEIGHT - TIME_PANEL_DATA_MARK_REFERENCE_TREND_HEIGHT),
    0,
    1,
  );
  const size = Math.round(
    TIME_PANEL_DATA_MARK_MIN_SIZE + ratio * (TIME_PANEL_DATA_MARK_MAX_SIZE - TIME_PANEL_DATA_MARK_MIN_SIZE),
  );

  return {
    size,
    offset: Math.max(1, Math.round(size * 0.14)),
    strokeWidth: size >= 12 ? 1.25 : 1,
    selectedStrokeWidth: size >= 12 ? 2 : 1.5,
  };
};
